import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip, message } from 'antd';
import { LoadingOutlined, UndoOutlined } from '@ant-design/icons';
import { undoActions } from '../../redux/actions';

/**
 * Undo button for the last saved value/formula change, plus a Ctrl+Z / Cmd+Z
 * shortcut while the button is mounted.
 *
 * @param onUndone  called with { label, valueIds, recalculated } after a successful undo
 * @param busy      extra condition that disables undo (e.g. a recalculation in progress)
 */
const UndoButton = ({ onUndone, busy = false, style }) => {
    const dispatch = useDispatch();
    const undoStack = useSelector((state) => state.undo.stack);
    const undoing = useSelector((state) => state.undo.undoing);

    const lastUndo = undoStack[undoStack.length - 1] ?? null;
    const disabled = !lastUndo || undoing || busy;

    const handleUndo = useCallback(async () => {
        if (disabled) return;

        try {
            const result = await dispatch(undoActions.undoLastChange());
            if (!result) return;

            onUndone?.(result);
            message.success(`Undone: ${result.label}`);
        } catch (err) {
            message.error(err.message);
        }
    }, [dispatch, disabled, onUndone]);

    // Ctrl+Z / Cmd+Z undoes the last saved change, unless the user is typing in a field.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;

            const target = e.target;
            const isTyping =
                target instanceof HTMLElement &&
                (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
            if (isTyping) return;

            e.preventDefault();
            handleUndo();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleUndo]);

    const tooltip = lastUndo
        ? `Undo ${lastUndo.label} (Ctrl+Z) · ${undoStack.length} change${undoStack.length === 1 ? '' : 's'} available`
        : 'Nothing to undo';

    return (
        <Tooltip title={tooltip}>
            <Button
                onClick={handleUndo}
                disabled={disabled}
                icon={undoing ? <LoadingOutlined /> : <UndoOutlined />}
                style={style}
            >
                Undo
            </Button>
        </Tooltip>
    );
};

export default UndoButton;
